import { useContext, useEffect, useState } from "react";
import {
  getProperties,
  createProperty,
  updateProperty,
  deleteProperty,
} from "../services/configurations/properties";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS";
type FilterType = "product" | "transaction";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

const filterSection = {
  product: "Products",
  transaction: "Transactions",
};

const handlePropertyActionFunction = (
  action: ActionType,
  filter: FilterType,
  t: Function
) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createProperty,
        fnMsg: t(`${filterSection[filter]}.propertyApiCreateLabel`),
      };
    case "UPDATE":
      return {
        actionFn: updateProperty,
        fnMsg: t(`${filterSection[filter]}.propertyApiUpdateLabel`),
      };
    case "DELETE":
      return {
        actionFn: deleteProperty,
        fnMsg: t(`${filterSection[filter]}.propertyApiDeleteLabel`),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useProperties = (type: UseType, filter: FilterType) => {
  const {
    t,
    toast,
    appState: { selectedApp },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const propertyAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handlePropertyActionFunction(action, filter, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA" && appid) {
      (async function () {
        await Promise.resolve(getProperties(appid, filter))
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type, filter]);

  return {
    data,
    propertyAction,
    reloadFn,
  };
};
